import React, { useState, useEffect } from 'react';
import './AdminDeposits.css';
import { useUser } from '../../context/UserContext';

const AdminDeposits = () => {
  const { apiCall } = useUser();
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  const loadPendingDeposits = async () => {
    try {
      setLoading(true);
      const response = await apiCall('deposits.php?action=pending_deposits');
      if (response.success) {
        setDeposits(response.data || []);
      }
    } catch (error) {
      console.error('Failed to load pending deposits:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPendingDeposits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = (deposit) => {
    setSelectedDeposit(deposit);
    setShowConfirmModal(true);
  };

  const handleConfirmDeposit = async (status = 'confirmed') => {
    if (!selectedDeposit) return;

    try {
      const response = await apiCall('deposits.php', 'POST', {
        action: 'confirm_deposit',
        deposit_id: selectedDeposit.id,
        admin_id: 'admin1', // Mock admin ID
        status: status, // 'confirmed' or 'rejected'
        admin_notes: adminNotes
      });

      if (response.success) {
        // Remove the deposit from the list
        setDeposits(prev => prev.filter(deposit => deposit.id !== selectedDeposit.id));
        setShowConfirmModal(false);
        setSelectedDeposit(null);
        setAdminNotes('');
        alert(`Deposit ${status} successfully`);
      }
    } catch (error) {
      console.error('Failed to confirm deposit:', error);
      alert(`Failed to ${status} deposit`);
    }
  };

  return (
    <div className="admin-deposits">
      <div className="admin-deposits__header">
        <h1 className="admin-deposits__title">Deposit Management</h1>
        <div className="admin-deposits__stats">
          <div className="admin-deposits__stat-card">
            <div className="admin-deposits__stat-value">{deposits.length}</div>
            <div className="admin-deposits__stat-label">Pending</div>
          </div>
          <button onClick={loadPendingDeposits} className="refresh-btn">
            Refresh
          </button>
        </div>
      </div>

      {/* Pending Deposits */}
      <div className="admin-deposits__section">
        <h2 className="admin-deposits__section-title">Pending Deposits</h2>
        {loading ? (
          <div className="loading">Loading deposits...</div>
        ) : deposits.length === 0 ? (
          <div className="admin-deposits__empty">
            <div className="admin-deposits__empty-icon">📋</div>
            <p>No pending deposits</p>
          </div>
        ) : (
          <div className="admin-deposits__table">
            <div className="admin-deposits__table-header">
              <div className="admin-deposits__table-cell">Date</div>
              <div className="admin-deposits__table-cell">User</div>
              <div className="admin-deposits__table-cell">Amount (USD)</div>
              <div className="admin-deposits__table-cell">Payment Method</div>
              <div className="admin-deposits__table-cell">BTC Address</div>
              <div className="admin-deposits__table-cell">TX Hash</div>
              <div className="admin-deposits__table-cell">Actions</div>
            </div>
            {deposits.map((deposit) => (
              <div key={deposit.id} className="admin-deposits__table-row">
                <div className="admin-deposits__table-cell">
                  {new Date(deposit.created_at).toLocaleDateString()}
                </div>
                <div className="admin-deposits__table-cell">
                  <div className="user-info">
                    <div className="username">{deposit.username || 'Unknown'}</div>
                    <div className="email">{deposit.email}</div>
                  </div>
                </div>
                <div className="admin-deposits__table-cell">
                  <span className="admin-deposits__amount">${deposit.amount_usd}</span>
                </div>
                <div className="admin-deposits__table-cell">
                  <span className="admin-deposits__currency">{deposit.payment_method?.toUpperCase()}</span>
                </div>
                <div className="admin-deposits__table-cell">
                  <span className="admin-deposits__address">
                    {deposit.btc_address ? `${deposit.btc_address.slice(0, 8)}...${deposit.btc_address.slice(-8)}` : 'N/A'}
                  </span>
                </div>
                <div className="admin-deposits__table-cell">
                  <span className="tx-hash">
                    {deposit.tx_hash ? (
                      <a
                        href={`https://blockstream.info/tx/${deposit.tx_hash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tx-link"
                      >
                        {deposit.tx_hash.substring(0, 8)}...
                      </a>
                    ) : 'N/A'}
                  </span>
                </div>
                <div className="admin-deposits__table-cell">
                  <button 
                    className="admin-deposits__confirm-btn"
                    onClick={() => handleConfirm(deposit)}
                  >
                    Review
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="admin-deposits__modal-overlay">
          <div className="admin-deposits__modal">
            <div className="admin-deposits__modal-header">
              <h3 className="admin-deposits__modal-title">Confirm Deposit</h3>
              <button 
                className="admin-deposits__modal-close"
                onClick={() => setShowConfirmModal(false)}
              >
                ×
              </button>
            </div>
            <div className="admin-deposits__modal-content">
              <div className="admin-deposits__modal-info">
                <div className="admin-deposits__modal-row">
                  <span>Deposit ID:</span>
                  <span className="admin-deposits__modal-value">{selectedDeposit?.id}</span>
                </div>
                <div className="admin-deposits__modal-row">
                  <span>User:</span>
                  <span className="admin-deposits__modal-value">
                    {selectedDeposit?.username || 'Unknown'} ({selectedDeposit?.email})
                  </span>
                </div>
                <div className="admin-deposits__modal-row">
                  <span>Amount (USD):</span>
                  <span className="admin-deposits__modal-value">
                    ${selectedDeposit?.amount_usd}
                  </span>
                </div>
                <div className="admin-deposits__modal-row">
                  <span>Payment Method:</span>
                  <span className="admin-deposits__modal-value">
                    {selectedDeposit?.payment_method?.toUpperCase()}
                  </span>
                </div>
                <div className="admin-deposits__modal-row">
                  <span>BTC Address:</span>
                  <span className="admin-deposits__modal-value">
                    {selectedDeposit?.btc_address || 'N/A'}
                  </span>
                </div>
                <div className="admin-deposits__modal-row">
                  <span>TX Hash:</span>
                  <span className="admin-deposits__modal-value">
                    {selectedDeposit?.tx_hash ? (
                      <a
                        href={`https://blockstream.info/tx/${selectedDeposit.tx_hash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {selectedDeposit.tx_hash}
                      </a>
                    ) : 'Not provided'}
                  </span>
                </div>
              </div>
              <div className="admin-deposits__modal-field">
                <label className="admin-deposits__modal-label">
                  Admin Notes (Optional):
                </label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="admin-deposits__modal-input"
                  placeholder="Add any notes about this deposit..."
                  rows="3"
                />
              </div>
              <div className="admin-deposits__modal-actions">
                <button
                  className="admin-deposits__modal-confirm"
                  onClick={() => handleConfirmDeposit('confirmed')}
                >
                  Confirm Deposit
                </button>
                <button
                  className="admin-deposits__modal-reject"
                  onClick={() => handleConfirmDeposit('rejected')}
                  style={{ backgroundColor: '#dc3545', marginLeft: '10px' }}
                >
                  Reject Deposit
                </button>
                <button 
                  className="admin-deposits__modal-cancel"
                  onClick={() => {
                    setShowConfirmModal(false);
                    setAdminNotes('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDeposits;